import * as THREE from "three";
import { MANNY_RIG } from "./mannyRig";
import { MALE_RIG } from "./maleRig";
import { rigProfile, type RigTable } from "./rigProfile";

const cache = new WeakMap<THREE.AnimationClip, WeakMap<RigTable, THREE.AnimationClip>>();
/** Normalized library clips use the male metric frame. Adapt once per body,
 * preserving shared source tracks and the simulation's ownership of the root. */
export function clipForBody(source: THREE.AnimationClip, target: RigTable): THREE.AnimationClip {
  let bodies = cache.get(source);
  if (!bodies) { bodies = new WeakMap(); cache.set(source, bodies); }
  const cached = bodies.get(target);
  if (cached) return cached;
  const from = rigProfile(MALE_RIG), to = rigProfile(target);
  const targetInv = to.rootRotation.clone().invert();
  const delta = new THREE.Vector3();
  const tracks: THREE.KeyframeTrack[] = [];
  for (const track of source.tracks) {
    const binding = /\.bones\[([^\]]+)\]\.(\w+)$/.exec(track.name);
    if (!binding || !to.names.has(binding[1]) || binding[1] === "root") continue;
    if (binding[2] === "position" && binding[1] === "pelvis" && target !== MALE_RIG) {
      const copy = track.clone();
      for (let i = 0; i < copy.values.length; i += 3) {
        delta.fromArray(track.values, i).sub(from.pelvis)
          .applyQuaternion(from.rootRotation).multiplyScalar(to.hipHeight / from.hipHeight)
          .applyQuaternion(targetInv).add(to.pelvis).toArray(copy.values, i);
      }
      tracks.push(copy);
    } else if (target === MALE_RIG && binding[2] === "quaternion" && /^(index|middle|ring|pinky|thumb)_0[123]_[lr]$/.test(binding[1])) {
      const sourceRest = MANNY_RIG.find(row => row[0] === binding[1])!;
      const targetRest = target.find(row => row[0] === binding[1])!;
      const offset = new THREE.Quaternion(...targetRest.slice(5) as [number,number,number,number])
        .multiply(new THREE.Quaternion(...sourceRest.slice(5) as [number,number,number,number]).invert());
      const copy = track.clone(), rotation = new THREE.Quaternion();
      for (let i = 0; i < copy.values.length; i += 4)
        rotation.fromArray(track.values, i).premultiply(offset).normalize().toArray(copy.values, i);
      tracks.push(copy);
    } else {
      tracks.push(track);
    }
  }
  // Separate clip container: never filter the global library in an Avatar.
  const clip = new THREE.AnimationClip(source.name, source.duration, tracks, source.blendMode);
  bodies.set(target, clip);
  return clip;
}

/** Rebind only at body changes. Clear every old binding BEFORE resetting rest
 * or activating new actions, so partial blends capture the new body's rest. */
export function rebindBodyActions(
  mixer: THREE.AnimationMixer,
  actions: Partial<Record<string, THREE.AnimationAction>>,
  sources: Partial<Record<string, THREE.AnimationClip>>,
  target: RigTable,
  resetRest: () => void,
): void {
  const snapshots = Object.entries(actions).flatMap(([key, action]) => {
    if (!action || !sources[key]) return [];
    return [{key, source: sources[key]!, time: action.time, scheduled: action.isScheduled(),
      enabled: action.enabled, paused: action.paused, weight: action.getEffectiveWeight(),
      timeScale: action.timeScale, loop: action.loop, repetitions: action.repetitions,
      clamp: action.clampWhenFinished}];
  });
  mixer.stopAllAction();
  mixer.uncacheRoot(mixer.getRoot());
  resetRest();
  for (const state of snapshots) {
    const action = mixer.clipAction(clipForBody(state.source, target));
    action.setLoop(state.loop, state.repetitions);
    action.clampWhenFinished = state.clamp;
    action.setEffectiveWeight(state.weight);
    action.timeScale = state.timeScale;
    if (state.scheduled) action.play();
    action.time = state.time;
    action.enabled = state.enabled;
    action.paused = state.paused;
    actions[state.key] = action;
  }
}
